import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { formatPhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import * as yup from "yup";

import Button from "@components/buttons/Button";
import BaseErrors from "@components/fields/BaseErrors";
import CheckboxGroup from "@components/fields/CheckboxGroup";
import DataSelect from "@components/fields/DataSelect";
import FormButtons from "@components/fields/FormButtons";
import PhoneInput from "@components/fields/PhoneInput";
import RadioButtonGroup from "@components/fields/RadioButtonGroup";
import RichTextarea from "@components/fields/RichTextarea";
import NativeCheckboxGroup, { Option } from "@components/fieldsNative/CheckboxGroup";
import styles from "@components/forms/ConsultForm.module.css";
import Form from "@components/forms/Form";
import FormCondition from "@components/forms/FormCondition";
import InviteAccountMembersForm from "@components/forms/InviteAccountMembersForm";
import Modal from "@components/Modal";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import useCareTeamInviteCreateMutation from "@hooks/useCareTeamInviteCreateMutation";
import useOpener, { Opener } from "@hooks/useOpener";
import {
  ConsultFragment as Consult,
  ConsultUpdateDocument,
  ConsultUpdateMutation,
  ConsultUpdateMutationVariables,
  ContactMethodEnum,
  GetJourneyWithExpertAndConsultsQuery,
  GetJourneyWithPlanQuery
} from "@typing/Generated";
import { handleMutationResponse } from "@utils/formUtils";
import { sortByStringField } from "@utils/modelUtils";
import { contactMethodOptions } from "@utils/optionUtils";

type FormData = {
  allInviteOptions: string[];
  contactMethod: ContactMethodEnum;
  inviteEmails: string[];
  invitedCareTeamInviteIds: string[];
  invitedClientIds: string[];
  languageCode: string | null;
  notes: string | null;
  phoneNumber?: string | null | undefined;
};

type Props = {
  consult: Consult;
  journey: GetJourneyWithExpertAndConsultsQuery["journey"] | GetJourneyWithPlanQuery["journey"];
  onCancel?: () => void;
  onSuccess?: (notes: string | null, invitedClientIds?: string[]) => Promise<void>;
  opener: Opener;
};

const ConsultUpdateForm = ({ consult, journey, onCancel, opener }: Props) => {
  const careTeamInviteCreate = useCareTeamInviteCreateMutation(journey);
  const intl = useIntl();
  const [updateConsult] = useMutation<ConsultUpdateMutation, ConsultUpdateMutationVariables>(ConsultUpdateDocument);
  const inviteOthersOpener = useOpener();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const currentClientRole = useMemo(
    () => journey.clientRoles?.find(role => role.client.id === journey.owner.id),
    [journey.clientRoles, journey.owner.id]
  );

  const inviteeOptions = useMemo(() => {
    const otherClientRoles = journey.clientRoles.filter(role => role.client.id !== journey.owner.id);
    if (currentClientRole) {
      const selfOption = [
        {
          label: currentClientRole.client.fullName,
          value: currentClientRole.client.id
        }
      ];
      return selfOption.concat(
        otherClientRoles.map(option => ({
          label: option.client.fullName,
          value: option.client.id
        }))
      );
    }

    return [];
  }, [journey.clientRoles, journey.owner.id, currentClientRole]);

  const initialPhoneNumber = useMemo(
    () => journey.owner.consultPhoneNumber ?? journey.owner.verificationPhoneNumber,
    [journey.owner.consultPhoneNumber, journey.owner.verificationPhoneNumber]
  );

  const defaultValues: FormData = {
    allInviteOptions:
      consult?.invites.filter(invite => invite.careTeamInvite).map(invite => invite.careTeamInvite?.id ?? "") ?? [],
    contactMethod: consult.contactMethod ?? ContactMethodEnum.ZOOM,
    inviteEmails: [],
    invitedCareTeamInviteIds:
      consult.invites.filter(invite => invite.careTeamInvite).map(invite => invite.careTeamInvite?.id ?? "") ?? [],
    invitedClientIds: consult.invites.filter(invite => invite.client !== null).map(invite => invite.client?.id ?? ""),
    languageCode: consult.languageCode ?? null,
    notes: consult?.notes ?? "",
    phoneNumber: consult.phoneNumber?.number ?? initialPhoneNumber?.number ?? null
  };

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      allInviteOptions: yup.array().defined().of(yup.string().defined()),
      contactMethod: yup
        .string()
        .oneOf(Object.values(ContactMethodEnum))
        .required(intl.formatMessage({ id: "errors.consult.contactMethod.blank" })),
      inviteEmails: yup.array().defined().of(yup.string().defined()),
      invitedCareTeamInviteIds: yup.array().defined().of(yup.string().defined()),
      invitedClientIds: yup.array().defined().of(yup.string().defined()),
      languageCode: yup.string().nullable().defined(),
      notes: yup.string().defined(),
      phoneNumber: yup.string().nullable()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema)
  });

  const contactMethod = form.watch("contactMethod");
  const inviteEmails = form.watch("inviteEmails");
  const invitedCareTeamInviteIds = form.watch("invitedCareTeamInviteIds");
  const allInviteOptions = form.watch("allInviteOptions");
  const invitedClientIds = form.watch("invitedClientIds");
  const phoneNumber = form.watch("phoneNumber");

  const onSubmit = useCallback(async () => {
    if (contactMethod === ContactMethodEnum.PHONE && !phoneNumber) {
      form.setError("phoneNumber", {
        message: intl.formatMessage({ id: `errors.consult.phoneNumber.blank` }),
        type: "validate"
      });
      return;
    }

    const notes = form.getValues().notes === "" ? null : form.getValues().notes;
    if (!form.getValues().invitedClientIds?.length && inviteeOptions) {
      form.setError("invitedClientIds", {
        type: "required"
      });
      return;
    }

    const careTeamInvites = await Promise.all(
      inviteEmails.map(async email =>
        careTeamInviteCreate({
          variables: {
            email,
            journeyId: journey.id
          }
        }).then(response => {
          if (response.data?.careTeamInviteCreate?.errors.length) {
            response.data.careTeamInviteCreate.errors.forEach(fieldError => {
              const messages = fieldError.errors.map(error =>
                intl.formatMessage({ id: `errors.inviteAccountMembers.${fieldError.field}.${error}` })
              );

              setBaseErrors(messages);
            });
          }
          return response;
        })
      )
    );
    if (baseErrors.length > 0) return;

    const invitedCareTeamInvites = invitedCareTeamInviteIds.concat(
      careTeamInvites.map(invite => invite.data?.careTeamInviteCreate?.careTeamInvite?.id ?? "")
    );

    return updateConsult({
      variables: {
        contactMethod,
        formattedNumber: phoneNumber ? formatPhoneNumber(phoneNumber) : null,
        id: consult.id,
        invitedCareTeamInviteIds: invitedCareTeamInvites,
        invitedClientIds: form.getValues().invitedClientIds,
        languageCode: form.getValues().languageCode,
        notes,
        number: phoneNumber
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.consultUpdate,
        extraBaseFields: ["email"],
        intl,
        onSuccess: () => {
          opener.close();
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.consult"
      });
    });
  }, [
    baseErrors.length,
    careTeamInviteCreate,
    consult.id,
    contactMethod,
    form,
    intl,
    inviteEmails,
    invitedCareTeamInviteIds,
    inviteeOptions,
    journey.id,
    opener,
    phoneNumber,
    updateConsult
  ]);

  const handleCareTeamInviteChange = (options: Option[]) => {
    form.setValue(
      "invitedCareTeamInviteIds",
      options.filter(option => option.type === "careTeamInvite").map(option => option.value)
    );

    form.setValue(
      "inviteEmails",
      options.filter(option => option.type === "email").map(option => option.value)
    );
  };

  const careTeamInvites = useMemo(() => {
    const options =
      journey.careTeamInvites
        ?.filter(invite => !invite.accepted && !invite.canceled)
        .map(invite => ({
          label: invite.email,
          type: "careTeamInvite",
          value: invite.id
        }))
        .concat(
          inviteEmails.map(email => ({
            label: email,
            type: "email",
            value: email
          }))
        ) ?? [];

    allInviteOptions.map(value => {
      if (!options.some(v => v.value === value)) {
        options.push({ label: value, type: "email", value });
      }
    });

    const seen = new Set();
    const uniqOptions = options.filter(el => {
      const duplicate = seen.has(el.label);
      seen.add(el.label);
      return !duplicate;
    });

    return sortByStringField(uniqOptions, o => o.value);
  }, [allInviteOptions, inviteEmails, journey.careTeamInvites]);

  const isValid = useMemo(() => {
    if (phoneNumber && phoneNumber !== "") {
      if (isValidPhoneNumber(phoneNumber)) {
        form.clearErrors("phoneNumber");
        return true;
      }
      return false;
    } else {
      return true;
    }
  }, [form, phoneNumber]);

  return (
    <div>
      <div className={inviteOthersOpener.isOpen ? "ion-hide" : ""}>
        <Form data-test="care-profile-notes-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
          <BaseErrors errors={baseErrors} />
          <div>
            <RadioButtonGroup<FormData>
              control={form.control}
              error={form.formState.errors.contactMethod}
              fullWidth
              label={intl.formatMessage({ id: "forms.consult.contactMethodLabel" })}
              name="contactMethod"
              options={contactMethodOptions(intl)}
            />
            <FormCondition condition={contactMethod === ContactMethodEnum.PHONE} fieldName="phoneNumber" form={form}>
              <PhoneInput<FormData>
                autofocus
                control={form.control}
                error={form.formState.errors.phoneNumber}
                label={intl.formatMessage({ id: "forms.consult.expertPhoneNumberLabel" })}
                name="phoneNumber"
              />
            </FormCondition>
            <div className={inviteeOptions ? "" : "ion-hide"}>
              <CheckboxGroup<FormData>
                control={form.control}
                errors={form.formState.errors.invitedClientIds}
                label={intl.formatMessage({ id: "forms.consult.invitedClientIdsLabel" })}
                name="invitedClientIds"
                options={inviteeOptions ?? []}
                required
              />
              {form.formState.errors.invitedClientIds && (
                <div className={styles.error}>
                  <FormattedMessage id="errors.consult.invitee" />
                </div>
              )}
            </div>
            <div>
              <div className={form.formState.isSubmitting ? "ion-hide" : ""}>
                <NativeCheckboxGroup<FormData>
                  errors={form.formState.errors.invitedClientIds}
                  label={intl.formatMessage({
                    id: inviteeOptions
                      ? "forms.consult.invitedCareTeamInviteIdsLabelOther"
                      : "forms.consult.invitedCareTeamInviteIdsLabel"
                  })}
                  name="invitedCareTeamInviteIds"
                  onChange={handleCareTeamInviteChange}
                  options={careTeamInvites ?? []}
                  required
                  subLabel={intl.formatMessage({ id: "forms.consult.invitedCareTeamInviteIdsSublabel" })}
                  values={careTeamInvites.filter(
                    invite => invitedCareTeamInviteIds.includes(invite.value) || inviteEmails.includes(invite.value)
                  )}
                />
                <Button fill="clear" onClick={inviteOthersOpener.open}>
                  <IconBase icon="add" slot="start" />
                  <FormattedMessage
                    id={inviteeOptions ? "forms.consult.addOtherInvites" : "forms.consult.addInvites"}
                  />
                </Button>
              </div>
              <div className={form.formState.isSubmitting ? "" : "ion-hide"}>
                <Icon icon="spinner" size="large" />
              </div>
            </div>
            <DataSelect<FormData>
              control={form.control}
              error={form.formState.errors.languageCode}
              hasNoneOption
              label={intl.formatMessage({ id: "forms.consult.languageCodeLabel" })}
              name="languageCode"
              type="languages"
            />
            <div>
              <RichTextarea<FormData>
                controls={[]}
                error={form.formState.errors.notes}
                form={form}
                journeyId={journey.id}
                label={intl.formatMessage({ id: "forms.consult.notesLabel" })}
                name="notes"
                placeholder={intl.formatMessage({ id: "dictionary.typeHere" })}
              />
            </div>
            <FormButtons hookForm={form} onCancel={onCancel} saveDisabled={!isValid} />
          </div>
        </Form>
      </div>
      <Modal
        opener={inviteOthersOpener}
        title={intl.formatMessage({ id: "models.client.inviteAccountMembers.consultTitle" })}
      >
        {({ modalDisplayed }) => (
          <InviteAccountMembersForm
            autofocus={modalDisplayed}
            consultScheduling={{
              inviteOptions: inviteeOptions,
              selectedClientIds:
                consult.invites.filter(invite => invite.client).map(invite => invite.client?.id as string) ?? []
            }}
            journey={journey}
            onCancel={inviteOthersOpener.close}
            onSuccess={(emails, clientIds) => {
              const newInviteEmails = [...inviteEmails].concat(emails ?? []);
              form.setValue(
                "inviteEmails",
                newInviteEmails.filter((v, i) => newInviteEmails.indexOf(v) === i)
              );
              if (clientIds) {
                const newClientIds = [...invitedClientIds].concat(clientIds);
                form.setValue(
                  "invitedClientIds",
                  newClientIds.filter((v, i) => newClientIds.indexOf(v) === i)
                );
              }
              inviteOthersOpener.close();
            }}
            saveKey="dictionary.add"
          />
        )}
      </Modal>
    </div>
  );
};

export default ConsultUpdateForm;
